'use client'

import { useEffect, useRef, ReactNode } from 'react'

interface ScrollAnimationProps {
  children: ReactNode
  className?: string
  threshold?: number
  rootMargin?: string
}

export function ScrollAnimation({
  children,
  className = '',
  threshold = 0.1,
  rootMargin = '0px',
}: ScrollAnimationProps) {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const element = ref.current
    if (!element) return

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible')
          }
        })
      },
      { threshold, rootMargin },
    )

    observer.observe(element)

    return () => {
      observer.unobserve(element)
    }
  }, [threshold, rootMargin])

  return (
    <div ref={ref} className={`animate-on-scroll ${className}`}>
      {children}
    </div>
  )
}

interface ScrollToTopButtonProps {
  showAfter?: number
}

export function ScrollToTopButton({ showAfter = 400 }: ScrollToTopButtonProps) {
  const buttonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    const handleScroll = () => {
      if (buttonRef.current) {
        buttonRef.current.style.opacity = window.scrollY > showAfter ? '1' : '0'
        buttonRef.current.style.pointerEvents = window.scrollY > showAfter ? 'auto' : 'none'
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()

    return () => window.removeEventListener('scroll', handleScroll)
  }, [showAfter])

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <button
      ref={buttonRef}
      onClick={scrollToTop}
      className="fixed bottom-8 end-8 z-40 cursor-pointer rounded-full border border-white/20 bg-white/10 p-3 text-white opacity-0 backdrop-blur-xl transition-all duration-300 hover:bg-white/15"
      aria-label="Scroll to top"
      style={{ pointerEvents: 'none' }}
    >
      <svg
        className="h-5 w-5 transition-transform duration-300 group-hover:-translate-y-1"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    </button>
  )
}
